import React, { useState, useEffect } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts'
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  FileText, 
  Calendar,
  BarChart3,
  PieChart as PieChartIcon
} from 'lucide-react'
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d']

const AnalyticsDashboard = ({ financialStatements = [] }) => {
  const [analytics, setAnalytics] = useState({
    totalStatements: 0,
    statementsByType: [],
    monthlyTrends: [],
    customerDistribution: [],
    revenueAnalysis: [],
    expenseAnalysis: []
  })

  useEffect(() => {
    if (financialStatements.length > 0) {
      generateAnalytics()
    }
  }, [financialStatements])

  const generateAnalytics = () => {
    // Basic statistics
    const totalStatements = financialStatements.length

    // Statements by type
    const typeCount = financialStatements.reduce((acc, statement) => {
      const type = statement.statement_type || statement.statementType
      acc[type] = (acc[type] || 0) + 1
      return acc
    }, {})

    const statementsByType = Object.entries(typeCount).map(([type, count]) => ({
      type: formatStatementType(type),
      count,
      percentage: ((count / totalStatements) * 100).toFixed(1)
    }))

    // Monthly trends (last 6 months)
    const monthlyData = generateMonthlyTrends()

    // Customer distribution
    const customerCount = financialStatements.reduce((acc, statement) => {
      const customer = statement.customer_name || statement.customerName || 'Unknown'
      acc[customer] = (acc[customer] || 0) + 1
      return acc
    }, {})

    const customerDistribution = Object.entries(customerCount)
      .map(([customer, count]) => ({ customer, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10) // Top 10 customers

    // Financial analysis for profit/loss statements
    const { revenueAnalysis, expenseAnalysis } = generateFinancialAnalysis()

    setAnalytics({
      totalStatements,
      statementsByType,
      monthlyTrends: monthlyData,
      customerDistribution,
      revenueAnalysis,
      expenseAnalysis
    })
  }

  const generateMonthlyTrends = () => {
    const last6Months = []
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(new Date(), i)
      const monthStart = startOfMonth(date)
      const monthEnd = endOfMonth(date)
      
      const monthStatements = financialStatements.filter(statement => {
        const statementDate = new Date(statement.created_at || statement.createdAt)
        return statementDate >= monthStart && statementDate <= monthEnd
      })

      last6Months.push({
        month: format(date, 'MMM yyyy'),
        statements: monthStatements.length,
        profit_loss: monthStatements.filter(s => (s.statement_type || s.statementType) === 'profit_loss').length,
        balance_sheet: monthStatements.filter(s => (s.statement_type || s.statementType) === 'balance_sheet').length
      })
    }
    return last6Months
  }

  const generateFinancialAnalysis = () => {
    const profitLossStatements = financialStatements.filter(
      s => (s.statement_type || s.statementType) === 'profit_loss'
    )

    if (profitLossStatements.length === 0) {
      return { revenueAnalysis: [], expenseAnalysis: [] }
    }

    // Aggregate revenue and expense data from flat structure
    const revenueData = {}
    const expenseData = {}

    profitLossStatements.forEach(statement => {
      const data = statement.financial_data || statement.data || {}
      
      // Process flat structure and categorize
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === 'string' && (value === '' || value === null)) return
        
        const numValue = parseFloat(value) || 0
        if (numValue === 0) return

        const lowerKey = key.toLowerCase()
        
        // Categorize based on key names
        if (lowerKey.includes('revenue') || (lowerKey.includes('income') && !lowerKey.includes('expense'))) {
          revenueData[key] = (revenueData[key] || 0) + numValue
        } else if (lowerKey.includes('expense') || lowerKey.includes('cost') || 
                   (lowerKey.includes('operating') && !lowerKey.includes('income'))) {
          expenseData[key] = (expenseData[key] || 0) + numValue
        }
      })
    })

    const revenueAnalysis = Object.entries(revenueData)
      .map(([category, amount]) => ({
        category: formatCategory(category),
        amount,
        formattedAmount: formatCurrency(amount)
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8)

    const expenseAnalysis = Object.entries(expenseData)
      .map(([category, amount]) => ({
        category: formatCategory(category),
        amount,
        formattedAmount: formatCurrency(amount)
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8)

    return { revenueAnalysis, expenseAnalysis }
  }

  const formatStatementType = (type) => {
    const types = {
      'profit_loss': 'Profit & Loss',
      'balance_sheet': 'Balance Sheet',
      'cash_flow': 'Cash Flow'
    }
    return types[type] || type
  }

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ')
                  .replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatCurrency = (amount) => {
    return `UGX ${amount.toLocaleString('en-UG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
          {trend && (
            <div className={`flex items-center mt-2 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {trendValue}
            </div>
          )}
        </div>
        <Icon className={`w-8 h-8 text-${color}-600`} />
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Statements"
          value={analytics.totalStatements}
          icon={FileText}
          color="blue"
        />
        <StatCard
          title="Active Customers"
          value={analytics.customerDistribution.length}
          icon={Users}
          color="green"
        />
        <StatCard
          title="Revenue Streams"
          value={analytics.revenueAnalysis.length}
          icon={DollarSign}
          color="yellow"
        />
        <StatCard
          title="This Month"
          value={analytics.monthlyTrends[analytics.monthlyTrends.length - 1]?.statements || 0}
          icon={Calendar}
          color="purple"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Statements by Type */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <PieChartIcon className="w-5 h-5 mr-2 text-blue-600" />
            <h3 className="text-lg font-semibold">Statements by Type</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.statementsByType}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ type, percentage }) => `${type} (${percentage}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {analytics.statementsByType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <BarChart3 className="w-5 h-5 mr-2 text-green-600" />
            <h3 className="text-lg font-semibold">Monthly Trends</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={analytics.monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="statements" stackId="1" stroke="#8884d8" fill="#8884d8" name="Total" />
              <Area type="monotone" dataKey="profit_loss" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="P&L" />
              <Area type="monotone" dataKey="balance_sheet" stackId="3" stroke="#ffc658" fill="#ffc658" name="Balance Sheet" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 gap-6">
        {/* Expense Analysis */}
        {analytics.expenseAnalysis.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Expense Categories</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.expenseAnalysis} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatCurrency} />
                <YAxis dataKey="category" type="category" width={100} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="amount" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Customer Distribution */}
      {analytics.customerDistribution.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Top Customers by Statements</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.customerDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="customer" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

export default AnalyticsDashboard
